import { useState } from "react";
import { useNavigate } from "react-router-dom";
import useKallitagLeads from "@/hooks/useKallitagLeads";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Nfc, RefreshCw, Loader2, Download, ArrowUpRight, Mail, Phone, MessageSquare, User, Inbox } from "lucide-react";
import { toast } from "sonner";

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return ""; }
}

export default function KallitagLeadsPage() {
  const { leads, loading, error, refresh } = useKallitagLeads();
  const navigate = useNavigate();
  const [importing, setImporting] = useState(null);

  const importLead = async (lead) => {
    if (lead.imported && lead.localLeadId) {
      navigate(`/lead/${lead.localLeadId}`);
      return;
    }
    setImporting(lead.id);
    try {
      const { data } = await api.post("/kallitag-leads/import", { kallitagLead: lead });
      toast.success("Lead importé — enrichissez-le avec un débrief vocal 🎙️");
      navigate(`/lead/${data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec de l'import");
    } finally {
      setImporting(null);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-10" data-testid="kallitag-leads-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
        <div>
          <div className="flex items-center gap-2">
            <span className="w-9 h-9 rounded-xl bg-sky-500/10 flex items-center justify-center"><Nfc className="w-5 h-5 text-sky-500" /></span>
            <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Leads NFC Kallitag</h1>
          </div>
          <p className="text-sm text-muted-foreground mt-2">Contacts laissés par les visiteurs sur vos profils NFC kallitag.fr. Importez-en un pour l'enrichir (débrief vocal, synthèse, relances).</p>
        </div>
        <Button onClick={refresh} variant="outline" disabled={loading} data-testid="refresh-kallitag-leads" className="rounded-xl">
          {loading ? <Loader2 className="w-4 h-4 klt-spin" /> : <><RefreshCw className="w-4 h-4 mr-2" /> Actualiser</>}
        </Button>
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center py-24 text-muted-foreground">
          <Loader2 className="w-7 h-7 klt-spin text-primary" />
          <p className="mt-3 text-sm">Chargement des leads Kallitag…</p>
        </div>
      ) : error ? (
        <div className="rounded-2xl border border-rose-200 dark:border-rose-900 bg-rose-500/5 p-6 text-center mt-6" data-testid="kallitag-leads-error">
          <p className="text-sm text-rose-600 dark:text-rose-400">{error}</p>
        </div>
      ) : leads.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border p-12 text-center mt-6" data-testid="kallitag-leads-empty">
          <Inbox className="w-8 h-8 mx-auto text-muted-foreground" />
          <p className="mt-3 font-medium">Aucun lead NFC pour l'instant</p>
          <p className="text-sm text-muted-foreground mt-1">Les contacts laissés sur vos cartes/profils NFC kallitag apparaîtront ici.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4 mt-6">
          {leads.map((lead) => (
            <div key={lead.id} data-testid="kallitag-lead-row" className="rounded-2xl border border-border bg-card p-5 shadow-sm flex flex-col">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 font-heading font-bold text-lg truncate">
                    <User className="w-4 h-4 text-primary shrink-0" /> {lead.name || "Contact"}
                  </div>
                  {lead.profile_slug && <p className="text-xs text-muted-foreground mt-0.5">Profil : {lead.profile_slug}</p>}
                </div>
                <span className="text-xs text-muted-foreground whitespace-nowrap">{fmtDate(lead.created_at)}</span>
              </div>

              <div className="mt-3 space-y-1.5 text-sm">
                {lead.email && <div className="flex items-center gap-2 text-muted-foreground"><Mail className="w-3.5 h-3.5" /> <span className="truncate">{lead.email}</span></div>}
                {lead.phone && <div className="flex items-center gap-2 text-muted-foreground"><Phone className="w-3.5 h-3.5" /> {lead.phone}</div>}
                {lead.message && (
                  <div className="flex items-start gap-2 text-foreground/80 rounded-xl bg-secondary/40 p-2.5 mt-1">
                    <MessageSquare className="w-3.5 h-3.5 mt-0.5 shrink-0" /> <span className="leading-relaxed">{lead.message}</span>
                  </div>
                )}
              </div>

              <div className="mt-4 pt-1">
                <Button
                  onClick={() => importLead(lead)}
                  disabled={importing === lead.id}
                  data-testid="import-kallitag-lead-button"
                  variant={lead.imported ? "outline" : "default"}
                  className="rounded-xl w-full"
                >
                  {importing === lead.id ? (
                    <Loader2 className="w-4 h-4 klt-spin" />
                  ) : lead.imported ? (
                    <><ArrowUpRight className="w-4 h-4 mr-2" /> Ouvrir la fiche</>
                  ) : (
                    <><Download className="w-4 h-4 mr-2" /> Importer & enrichir</>
                  )}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
